import {useState} from 'react';
import {
  AssetsFiltersFE,
  AssetsOptionLabelValueFilters,
  isStaticAssetsFilters,
  StaticAssetsFilters,
} from '../types/internal/tables/assetsTable';
import {isUndefined} from 'lodash';

export default function useAssetsFilters() {
  const [filters, setFilters] = useState<AssetsFiltersFE>({
    building_type: [{label: 'Strumentali Sì Risorse', value: 'SSR'}, {label: 'Strumentali No Risorse', value: 'SNR'}],
    is_closing_date_null: true,
    total_uog_contacts_min: 1,
    event_cluster: [{label: "Valore non presente", value: "null"}]
  });
  const [filtersInExcludeMode, setFiltersInExcludeMode] = useState<AssetsOptionLabelValueFilters[]>(["event_cluster"]);
  
  function addFilters(addFilters: AssetsFiltersFE) {
    setFilters(filters => {
      const {
        hazards,
        alert_parameters,
        ...otherFilters
      } = addFilters;
      
      let updatedHazards = {...filters.hazards};
      let updatedAlertParameters = {...filters.alert_parameters}
      
      if (hazards) {
        for (const [key, value] of Object.entries(hazards)) {
          updatedHazards = {
            ...updatedHazards,
            [key]: value,
          };
        }
      }
      
      if (alert_parameters) {
        for (const [key, value] of Object.entries(alert_parameters)) {
          updatedAlertParameters = {
            ...updatedAlertParameters,
            [key]: value,
          };
        }
      }
      
      return {
        ...filters,
        ...otherFilters,
        hazards: updatedHazards,
        alert_parameters: updatedAlertParameters
      };
    });
  }
  
  function removeFilters(
    removeFilters: ((StaticAssetsFilters | 'hazards' | 'alert_parameters') | string)[] | ((StaticAssetsFilters | 'hazards' | 'alert_parameters') | string)
  ) {
    setFilters(filters => {
      const updatedFilters = {...filters};
      
      if (Array.isArray(removeFilters)) {
        removeFilters.forEach(el => {
          if (isStaticAssetsFilters(el) || el === 'hazards' || el === 'alert_parameters') {
            delete updatedFilters[el];
          } else {
            delete updatedFilters?.hazards?.[el];
            delete updatedFilters?.alert_parameters?.[el]
            delete updatedFilters?.overall_land_coverage?.[el];
          }
        });
      } else {
        if (isStaticAssetsFilters(removeFilters) || removeFilters === 'hazards' || removeFilters === 'alert_parameters') {
          delete updatedFilters[removeFilters];
        } else {
          delete updatedFilters?.hazards?.[removeFilters];
          delete updatedFilters?.alert_parameters?.[removeFilters]
          delete updatedFilters?.overall_land_coverage?.[removeFilters];
        }
      }
      
      return updatedFilters;
    });
  }
  
  function setOverallLandCoverageNullFilter(source: string) {
    setFilters(filters => {
      const filtersToUpdate = {
        ...filters
      }
      
      if (!filtersToUpdate.overall_land_coverage ) {
        filtersToUpdate.overall_land_coverage = {}
      }
      
      if (!filtersToUpdate.overall_land_coverage[source]) {
        filtersToUpdate.overall_land_coverage[source] = {};
      }
      
      filtersToUpdate.overall_land_coverage[source].is_null = true;
      delete filtersToUpdate?.overall_land_coverage?.[source]?.min;
      delete filtersToUpdate?.overall_land_coverage?.[source]?.max;
      
      return filtersToUpdate;
    });
  }
  
  function removeOverallLandCoverageFilter(source: string) {
    setFilters(filters => {
      const updatedFilters = {...filters};
      delete updatedFilters?.overall_land_coverage?.[source];
      return updatedFilters;
    });
  }
  
  function setOverallLandCoverageMinFilter(source: string, value: number) {
    setFilters(filters => {
      const filtersToUpdate = {
        ...filters
      }
      
      if (!filtersToUpdate.overall_land_coverage) {
        filtersToUpdate.overall_land_coverage = {}
      }
      
      if (!filtersToUpdate.overall_land_coverage[source]) {
        filtersToUpdate.overall_land_coverage[source] = {};
      }
      
      filtersToUpdate.overall_land_coverage[source].min = value;
      
      return filtersToUpdate;
    });
  }
  
  function removeOverallLandCoverageMinFilter(source: string) {
    setFilters(filters => {
      const updatedFilters = {...filters};
      
      if (isUndefined(updatedFilters?.overall_land_coverage?.[source]?.max)) {
        delete updatedFilters?.overall_land_coverage?.[source];
      } else {
        delete updatedFilters?.overall_land_coverage?.[source]?.min;
      }
      
      return updatedFilters;
    });
  }
  
  function setOverallLandCoverageMaxFilter(source: string, value: number) {
    setFilters(filters => {
      const filtersToUpdate = {
        ...filters
      }
      
      if (!filtersToUpdate.overall_land_coverage ) {
        filtersToUpdate.overall_land_coverage = {}
      }
      if (!filtersToUpdate.overall_land_coverage[source]) {
        filtersToUpdate.overall_land_coverage[source] = {};
      }
      filtersToUpdate.overall_land_coverage[source].max = value;
      
      return filtersToUpdate;
    });
  }
  
  function removeOverallLandCoverageMaxFilter(source: string) {
    setFilters(filters => {
      const updatedFilters = {...filters};
      
      if (isUndefined(updatedFilters?.overall_land_coverage?.[source]?.min)) {
        delete updatedFilters?.overall_land_coverage?.[source];
      } else {
        delete updatedFilters?.overall_land_coverage?.[source]?.max;
      }
      
      return updatedFilters;
    });
  }
  
  function resetFilters() {
    setFilters(filters => ({}));
  }
  
  function insertFilterInExcludeMode(filterInExcludeMode: AssetsOptionLabelValueFilters) {
    setFiltersInExcludeMode(filtersInExcludeMode => [...filtersInExcludeMode, filterInExcludeMode]);
  }
  
  function removeFilterInExcludeMode(filterInExcludeMode: AssetsOptionLabelValueFilters) {
    setFiltersInExcludeMode(filtersInExcludeMode => {
      const indexToRemove = filtersInExcludeMode.indexOf(filterInExcludeMode);
      
      if (indexToRemove === -1) {
        return filtersInExcludeMode;
      }
      
      const updatedFiltersInExcludeMode = [...filtersInExcludeMode];
      updatedFiltersInExcludeMode.splice(indexToRemove, 1);
      
      return updatedFiltersInExcludeMode;
    });
  }
  
  return {
    filters,
    addFilters,
    removeFilters,
    setOverallLandCoverageNullFilter,
    removeOverallLandCoverageFilter,
    setOverallLandCoverageMinFilter,
    removeOverallLandCoverageMinFilter,
    setOverallLandCoverageMaxFilter,
    removeOverallLandCoverageMaxFilter,
    resetFilters,
    filtersInExcludeMode,
    insertFilterInExcludeMode,
    removeFilterInExcludeMode,
  };
}